import { createSlice } from '@reduxjs/toolkit';
import { SearchStatus } from '../../constants';

const getInitialTripState = () => ({
  quotesStatus: SearchStatus.INITIAL,
  quotes: [],
  quotesErrors: undefined,
  quotesIneligibility: undefined,
  selectedQuote: undefined,
  optionsFormValidations: {},
  doSaveAgentId: false,
  hasNoArchRoamAccount: false,
  tripInfo: {
    tripCost: undefined,
    startDate: undefined,
    endDate: undefined,
    depositDate: undefined,
    destinations: [],
    formValidations: {},
  },
});

const insuranceSlice = createSlice({
  name: 'insurance',
  initialState: {
    trips: {},
  },
  reducers: {
    // =============================
    // GET QUOTES
    // =============================
    getInsuranceQuotesRequest: (state, { payload: { tripId } }) => {
      const trip = state.trips[tripId] || getInitialTripState();

      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: {
            ...trip,
            quotesStatus: SearchStatus.SEARCHING,
            quotesErrors: null,
            quotesIneligibility: null,
            quotesInvalidAgentId: false,
          },
        },
      };
    },

    getInsuranceQuotesSuccess: (
      state,
      { payload: { tripId, quotes, retrievedAt, ineligibilityMessages } },
    ) => {
      const trip = state.trips[tripId] || getInitialTripState();

      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: {
            ...trip,
            quotes,
            quotesStatus: SearchStatus.DONE,
            quotesRetrievedAt: retrievedAt || new Date().toISOString(),
            quotesErrors: null,
            quotesIneligibility: ineligibilityMessages || null,
            quotesInvalidAgentId: false,
          },
        },
      };
    },

    getInsuranceQuotesFailure: (
      state,
      { payload: { tripId, errors, ineligibilityMessages, invalidAgentId } },
    ) => {
      const trip = state.trips[tripId] || getInitialTripState();

      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: {
            ...trip,
            quotesStatus: SearchStatus.ERROR,
            quotesErrors: errors,
            quotesIneligibility: ineligibilityMessages || null,
            quotesInvalidAgentId: invalidAgentId || false,
            quotes: [],
            selectedQuote: null,
          },
        },
      };
    },

    // =============================
    // SELECT PLAN
    // =============================
    setSelectedInsurancePlanId: (state, { payload: { tripId, planId } }) => {
      const trip = state.trips[tripId] || getInitialTripState();

      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: {
            ...trip,
            selectedQuote: {
              planId,
              upgrades: {},
              selectedAt: new Date().toISOString(),
            },
          },
        },
      };
    },

    clearInsuranceQuotes: (state, { payload: { tripId } }) => {
      const trip = state.trips[tripId];
      if (!trip) return state;

      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: {
            ...trip,
            quotes: [],
            quotesStatus: SearchStatus.INITIAL,
            quotesErrors: undefined,
            quotesIneligibility: undefined,
          },
        },
      };
    },

    clearInsuranceSelection: (state, { payload: { tripId } }) => {
      const trip = state.trips[tripId];
      if (!trip) return state;

      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: {
            ...trip,
            selectedQuote: null,
          },
        },
      };
    },

    upgradeSelectedQuote: (state, { payload: { tripId, planId, upgradeCode } }) => {
      const trip = state.trips[tripId];
      if (!trip?.selectedQuote || trip.selectedQuote.planId !== planId) {
        return state;
      }

      const currentUpgrades = trip.selectedQuote.upgrades || {};
      const currentValue = currentUpgrades[upgradeCode] ?? false;

      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: {
            ...trip,
            selectedQuote: {
              ...trip.selectedQuote,
              upgrades: {
                ...currentUpgrades,
                [upgradeCode]: !currentValue,
              },
            },
          },
        },
      };
    },

    // =============================
    // TripInfo
    // =============================
    setTripInfo: (state, { payload: { tripId, tripInfo } }) => {
      const trip = state.trips[tripId] || getInitialTripState();
      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: {
            ...trip,
            tripInfo: {
              ...trip.tripInfo,
              ...tripInfo,
            },
          },
        },
      };
    },
    setTripInfoValidationErrors: (state, { payload: { tripId, errors } }) => {
      const trip = state.trips[tripId] || getInitialTripState();
      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: {
            ...trip,
            tripInfo: {
              ...trip.tripInfo,
              formValidations: errors,
            },
          },
        },
      };
    },

    // =============================
    // AGENT ID
    // =============================
    setInsuranceSaveAgentId: (state, { payload: { tripId, doSaveAgentId } }) => {
      const trip = state.trips[tripId] || getInitialTripState();
      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: { ...trip, doSaveAgentId: !!doSaveAgentId },
        },
      };
    },
    setInsuranceHasNoArchRoamAccount: (state, { payload: { tripId, hasNoArchRoamAccount } }) => {
      const trip = state.trips[tripId] || getInitialTripState();
      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: { ...trip, hasNoArchRoamAccount },
        },
      };
    },

    // =============================
    // INSURANCE OPTIONS
    // =============================
    setInsuranceOptionsFormValidations: (state, { payload: { tripId, errors } }) => {
      const trip = state.trips[tripId] || getInitialTripState();
      return {
        ...state,
        trips: {
          ...state.trips,
          [tripId]: {
            ...trip,
            optionsFormValidations: errors,
          },
        },
      };
    },

    // =============================
    // CLEAR
    // =============================
    clearInsuranceData: (state, { payload: { tripId } }) => {
      const newTrips = { ...state.trips };
      delete newTrips[tripId];

      return {
        ...state,
        trips: newTrips,
      };
    },
  },
});

export const selectInsuranceInfo =
  ({ tripId }) =>
  (state) => {
    const tripInsurance = state.insurance?.trips?.[tripId];
    if (!tripInsurance?.selectedQuote?.planId) {
      return null;
    }

    const selectedQuote = (tripInsurance.quotes || []).find(
      (q) => q.planId === tripInsurance.selectedQuote.planId,
    );

    if (!selectedQuote) return null;

    const selectedUpgrades = (selectedQuote.coverage?.quotedOptions || []).filter(
      (option) => tripInsurance.selectedQuote.upgrades?.[option.coverageCode],
    );

    return {
      quote: selectedQuote,
      upgrades: selectedUpgrades,
    };
  };

export const selectInsuranceSelectedQuote =
  ({ tripId }) =>
  (state) => {
    const selectedQuote = state.insurance.trips[tripId]?.selectedQuote;
    return selectedQuote;
  };

export const selectInsuranceQuotes =
  ({ tripId }) =>
  (state) => {
    const selectedQuote = state.insurance.trips[tripId]?.quotes || [];
    return selectedQuote;
  };

export const selectInsuranceQuotesStatus =
  ({ tripId }) =>
  (state) => {
    const selectedQuote = state.insurance.trips[tripId]?.quotesStatus || SearchStatus.INITIAL;
    return selectedQuote;
  };

export const selectInsuranceQuotesErrors =
  ({ tripId }) =>
  (state) => {
    const quotesErrors = state.insurance.trips[tripId]?.quotesErrors;
    return quotesErrors;
  };

export const selectInsuranceQuotesIneligibility =
  ({ tripId }) =>
  (state) => {
    const quotesIneligibility = state.insurance.trips[tripId]?.quotesIneligibility;
    return quotesIneligibility;
  };

export const selectInsuranceTripInfo =
  ({ tripId }) =>
  (state) => {
    const tripInfo = state.insurance.trips[tripId]?.tripInfo;
    return tripInfo;
  };

export const selectInsuranceOptionsFormValidations =
  ({ tripId }) =>
  (state) => {
    const optionsFormValidations = state.insurance.trips[tripId]?.optionsFormValidations;
    return optionsFormValidations;
  };

export const selectInsuranceSaveAgentId =
  ({ tripId }) =>
  (state) =>
    state.insurance.trips[tripId]?.doSaveAgentId ?? false;

export const selectInsuranceQuotesInvalidAgentId =
  ({ tripId }) =>
  (state) =>
    state.insurance.trips[tripId]?.quotesInvalidAgentId ?? false;

export const selectInsuranceHasNoArchRoamAccount =
  ({ tripId }) =>
  (state) =>
    state.insurance.trips[tripId]?.hasNoArchRoamAccount ?? false;

export const {
  getInsuranceQuotesRequest,
  getInsuranceQuotesSuccess,
  getInsuranceQuotesFailure,
  setTripInfo,
  setTripInfoValidationErrors,
  clearInsuranceQuotes,
  clearInsuranceSelection,
  upgradeSelectedQuote,
  setSelectedInsurancePlanId,
  clearInsuranceData,
  setInsuranceOptionsFormValidations,
  setInsuranceSaveAgentId,
  setInsuranceHasNoArchRoamAccount,
} = insuranceSlice.actions;
export default insuranceSlice.reducer;
